import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { SERVICES } from "../../data/content";
import { SectionLabel } from "./Shared";

export default function Services({ onScrollTo }) {
  const [active, setActive] = useState(null);

  return (
    <section id="services" className="relative py-24 md:py-32 border-t border-white/5" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <SectionLabel num="[ 03 ]">Our Services</SectionLabel>
            <h2 className="font-display font-extrabold text-white tracking-tighter leading-[0.95] text-4xl md:text-6xl max-w-3xl">
              Full-stack telecom capability, one accountable partner.
            </h2>
          </div>
          <p className="max-w-xs text-sm leading-relaxed" style={{ color: "#64748B" }}>
            Nine integrated verticals spanning the complete deployment lifecycle —
            from civil foundations to fiber, radio and lifecycle O&M.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.button
                key={s.id}
                onClick={() => setActive(s)}
                data-testid={`service-card-${s.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={`card-hover group relative text-left bg-[#0C1322] border border-white/10 p-7 min-h-[220px] flex flex-col justify-between overflow-hidden ${s.span}`}
              >
                {s.image && (
                  <div className="absolute inset-0 opacity-[0.12] group-hover:opacity-25 transition-opacity duration-500">
                    <img src={s.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="relative flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-mono-accent text-cyan text-sm" style={{ color: "#00F0FF" }}>{s.index}</span>
                    <Icon size={26} className="text-white/80" />
                  </div>
                  <ArrowUpRight size={20} className="text-tertiary cta-arrow" style={{ color: "#64748B" }} />
                </div>
                <div className="relative mt-8">
                  <span className="mono-label" style={{ color: "#64748B" }}>{s.tagline}</span>
                  <h3 className="mt-3 font-display font-extrabold text-white tracking-tight text-2xl md:text-3xl leading-none">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed max-w-lg" style={{ color: "#94A3B8" }}>
                    {s.summary}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="bg-[#0C1322] border border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="service-dialog"
        >
          {active && (
            <>
              <DialogHeader>
                <span className="mono-label text-cyan text-left" style={{ color: "#00F0FF" }}>
                  {active.index} · {active.tagline}
                </span>
                <DialogTitle className="font-display font-extrabold text-3xl tracking-tight text-white text-left">
                  {active.title}
                </DialogTitle>
                <DialogDescription className="text-left leading-relaxed" style={{ color: "#94A3B8" }}>
                  {active.summary}
                </DialogDescription>
              </DialogHeader>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {active.items.map((it, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm" style={{ color: "#94A3B8" }}>
                    <Check size={16} className="text-cyan shrink-0 mt-0.5" style={{ color: "#00F0FF" }} />
                    {it}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { setActive(null); onScrollTo("contact"); }}
                data-testid="service-dialog-quote-btn"
                className="group mt-6 flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-6 py-3.5 mono-label transition-colors duration-300"
              >
                Request a Quote for this Service
                <ArrowUpRight size={16} className="cta-arrow" />
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
